"use client";

import { Icon } from "@/components/shared/icon";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";

interface PostHogErrorBoundaryProps {
  children: React.ReactNode;
}

function PostHogErrorFallback({
  error,
  resetError,
}: {
  error: Error;
  resetError: () => void;
}) {
  const posthog = usePostHog();

  useEffect(() => {
    // Capture error in PostHog
    posthog?.capture("application_client_error", {
      error_message: error.message,
      error_stack: error.stack,
      error_name: error.name,
      url: window.location.href,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });
  }, [error, posthog]);

  return (
    <div className="flex min-h-[400px] items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <Icon icon={faExclamationTriangle} size="xl" className="text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-card-foreground">
              Oops! Something went wrong
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              We've been notified about this error and are working to fix it.
            </p>
          </div>
        </div>
        
        <div className="mt-4">
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Error details
            </summary>
            <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground">
              {error.message}
            </pre>
          </details>
        </div>
        
        <div className="mt-4">
          <button
            onClick={resetError}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}

export function PostHogErrorBoundary({ children }: PostHogErrorBoundaryProps) {
  return (
    <ErrorBoundary fallback={PostHogErrorFallback}>
      {children}
    </ErrorBoundary>
  );
}
